import React, { useState } from "react";
import { Check, ChevronDown, CircleSlash, Plus } from "lucide-react";
import { useAgents } from "@/shared/api/agents";
import PixelAvatar from "@/shared/components/PixelAvatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/shared/components/ui/command";
import { menuLabel, menuRow } from "@/shared/components/menuStyles";
import { cn } from "@/shared/lib/cn";
import { pickOf } from "./contextPick";
import type { ContextPick } from "./types";

// the shared menu row, highlighted the way cmdk marks its selected row
const row = cn(menuRow, "data-[selected=true]:bg-[var(--accent-hover)]");

/* Builder's agent picker, on the left of its composer's strip: a quiet strip link that shows the
   picked agent, and a searchable list of the workspace's agents (amber avatars have unpublished
   changes), "No agent" above them and "New agent" below. */
export default function AgentContextPicker({ value, onChange, onNewAgent }: {
  value: ContextPick;
  onChange: (pick: ContextPick) => void;
  onNewAgent: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { data: agents = [] } = useAgents();
  const picked = value?.kind === "agent" ? value.agent : null;
  // the list's row when it has one: its avatar; an agent just made may not be in it yet
  const seed = picked && agents.find((agent) => agent.id === picked.id)?.seed;
  const choose = (pick: ContextPick) => { onChange(pick); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={picked ? `Builder works on ${picked.name}` : "Choose the agent Builder works on"}
          className="press h-[26px] max-w-full min-w-0 px-1.5 rounded-[8px] flex items-center gap-1.5 text-[11.5px] whitespace-nowrap text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)] data-[state=open]:bg-[var(--accent-hover)] data-[state=open]:text-[var(--text-primary)]"
        >
          {value?.kind === "new" ? (
            <span className="w-4 h-4 shrink-0 rounded-[5px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] flex items-center justify-center">
              <Plus className="w-3 h-3" strokeWidth={2} />
            </span>
          ) : picked ? (
            <PixelAvatar seed={seed ?? picked.id} size={16} ring />
          ) : (
            <span className="w-4 h-4 shrink-0 rounded-[5px] border border-dashed border-[var(--text-quaternary)]" />
          )}
          <span className="truncate min-w-0">{value?.kind === "new" ? "New agent" : picked ? picked.name : "Choose an agent"}</span>
          <ChevronDown className="w-3 h-3 shrink-0 text-[var(--text-tertiary)]" strokeWidth={2} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" collisionPadding={12} className="w-[min(316px,90vw)] p-0 rounded-[14px] bg-[var(--surface)] text-[var(--text-primary)] ring-1 ring-[var(--hairline-strong)] border-0 shadow-menu overflow-hidden">
        <Command label="Find an agent" className="h-auto bg-transparent text-[var(--text-primary)]">
          <CommandInput placeholder="Find an agent" className="text-[13px] h-8 py-1.5" />
          <CommandList className="max-h-[300px] p-1">
            <CommandEmpty>No agent by that name.</CommandEmpty>
            <CommandItem value="none" keywords={["no agent"]} onSelect={() => choose(null)} className={row}>
              <CircleSlash className="!w-4 !h-4 text-[var(--text-secondary)]" strokeWidth={1.75} />
              <span>No agent</span>
              <span className="ml-auto pl-3 text-[11.5px] text-[var(--text-tertiary)]">Builder asks which one</span>
              <Check className={cn("!w-3.5 !h-3.5 shrink-0", value ? "invisible" : "")} />
            </CommandItem>
            {agents.length > 0 && (
              <CommandGroup heading={<span className={cn(menuLabel, "block")}>Your agents</span>} className="p-0 [&_[cmdk-group-heading]]:p-0">
                {agents.map((agent) => (
                  <CommandItem key={agent.id} value={agent.id} keywords={[agent.name]} onSelect={() => choose(pickOf(agent))} className={row}>
                    <PixelAvatar seed={agent.seed} size={20} ring />
                    <span className="truncate min-w-0">{agent.name}</span>
                    <span className="ml-auto pl-3 shrink-0 text-[11.5px] text-[var(--text-tertiary)]">
                      {agent.has_unpublished_changes && <span className="text-[var(--warn-text)]">Unpublished · </span>}
                      {agent.published_version != null && <span className="font-mono tabular-nums">v{agent.published_version}</span>}
                    </span>
                    <Check className={cn("!w-3.5 !h-3.5 shrink-0", picked?.id === agent.id ? "" : "invisible")} />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
          <div className="border-t border-[var(--hairline)] p-1">
            <button type="button" onClick={() => { setOpen(false); onNewAgent(); }} className={cn(menuRow, "w-full text-left hover:bg-[var(--accent-hover)]")}>
              <Plus className="w-4 h-4 text-[var(--text-secondary)]" strokeWidth={1.75} />
              <span>New agent</span>
              <span className="ml-auto pl-3 text-[11.5px] text-[var(--text-tertiary)]">Builder makes it</span>
            </button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
